/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useRef, useEffect } from "react";
import { X, Send, Bot, User as UserIcon, Sparkles } from "lucide-react";
import { cn } from "../utils/cn";
import { buildApiUrl } from "../config/api";
import { OpenAPISpec, ParsedEndpoint } from "../types";
import { FormattedMessage } from "./FormattedMessage";

interface AIChatProps {
  isOpen: boolean;
  onClose: () => void;
  spec: OpenAPISpec;
  endpoints: ParsedEndpoint[];
  token: string | null;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

export function AIChat({ isOpen, onClose, spec, endpoints, token }: AIChatProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "assistant",
      content: `Hello! I'm your AI assistant for the ${spec.info.title} API. I can help you:\n\n• Find the right endpoints for your needs\n• Explain how to use specific endpoints\n• Answer questions about the API\n• Generate code examples\n\nWhat would you like to know?`,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const res = await fetch(buildApiUrl("/api/ai/chat"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          message: userMessage.content,
          spec: {
            title: spec.info.title,
            description: spec.info.description,
            endpoints: endpoints.map((e) => ({
              method: e.method,
              path: e.path,
              summary: e.operation.summary,
              description: e.operation.description,
              tags: e.operation.tags,
            })),
          },
          history: messages.slice(-10).map((m) => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });

      if (!res.ok) throw new Error("Failed to get response");

      const data = await res.json();

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: data.response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "I'm sorry, I encountered an error processing your request. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-2xl h-full sm:h-[80vh] bg-white sm:border-2 sm:border-zinc-900 shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b-2 border-zinc-900 bg-zinc-50">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-zinc-900 border-2 border-zinc-900 flex items-center justify-center text-white">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900 text-sm sm:text-base tracking-tight">AI Assistant</h3>
              <p className="text-[10px] sm:text-xs text-zinc-500 tracking-wide">POWERED BY QWEN</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200 border border-transparent hover:border-zinc-900 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 bg-white">
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                "flex gap-3 sm:gap-4",
                message.role === "user" ? "flex-row-reverse" : "flex-row"
              )}
            >
              <div
                className={cn(
                  "w-8 h-8 border-2 flex items-center justify-center shrink-0",
                  message.role === "user"
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-900 border-zinc-900"
                )}
              >
                {message.role === "user" ? (
                  <UserIcon className="w-4 h-4" />
                ) : (
                  <Bot className="w-4 h-4" />
                )}
              </div>
              <div
                className={cn(
                  "max-w-[80%] border-2 px-4 py-3",
                  message.role === "user"
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-zinc-50 text-zinc-900 border-zinc-200"
                )}
              >
                {message.role === "user" ? (
                  <span className="text-sm leading-relaxed whitespace-pre-wrap">
                    {message.content}
                  </span>
                ) : (
                  <FormattedMessage content={message.content} />
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex gap-3 sm:gap-4">
              <div className="w-8 h-8 border-2 border-zinc-900 bg-white text-zinc-900 flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4" />
              </div>
              <div className="bg-zinc-50 border-2 border-zinc-200 px-4 py-3 flex items-center gap-2">
                <div className="w-2 h-2 bg-zinc-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                <div className="w-2 h-2 bg-zinc-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                <div className="w-2 h-2 bg-zinc-400 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-3 sm:p-4 border-t-2 border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-2 sm:gap-3 bg-white border-2 border-zinc-200 px-3 sm:px-4 py-2.5 sm:py-3 focus-within:border-zinc-900 focus-within:bg-white transition-colors">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about the API..."
              className="flex-1 bg-transparent border-none outline-none text-zinc-900 placeholder-zinc-400 text-sm"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="p-2 bg-zinc-900 text-white border-2 border-zinc-900 hover:bg-white hover:text-zinc-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
